import React, { useCallback, useContext, useState } from 'react';
import Dialog from '@mui/material/Dialog';
import { Badge, Card, PageHeader } from '../design-system';
import UserProfileContext from '../context/userprofilecontext.js';
import DocumentPanel from '../components/center-panel/DocumentPanel';
import { useConfirmAction } from '../components/ui/confirmactionhandler';
import { deleteLiviDocument, renameLiviDocument, useLiviDocuments } from '../api/liviDocuments';
import { useCaseBucketName } from '../api/casebucket';
import { DOCUMENT_FORMATS } from '../utils/caseObjectDownload';
import { formatDateTime, formatFileSize } from '../utils/formatters';
import styles from './page-livi-files.module.css';

const FORMAT_FILTERS = [
  { value: '', label: 'All files' },
  { value: 'docx', label: 'Word' },
  { value: 'xlsx', label: 'Excel' },
];

const toDate = (value) => {
  if (!value) return null;
  const date = value instanceof Date ? value : new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
};

// A refused request (a Viewer renaming, say) reads as a sentence, not a status code.
const actionError = (err, verb) => {
  const status = (err?._response || err?.response)?.statusCode;
  if (status === 403) return `You don’t have permission to ${verb} files on this case.`;
  if (status === 404) return 'That file no longer exists. The list has been refreshed.';
  return `The file could not be ${verb === 'rename' ? 'renamed' : 'deleted'}. Please try again.`;
};

function RenameDialog({ doc, onClose, onSaved }) {
  const [title, setTitle] = useState(doc?.title || '');
  const [state, setState] = useState({ saving: false, error: null });

  const save = async (event) => {
    event.preventDefault();
    const trimmed = title.trim();
    if (!trimmed) {
      setState({ saving: false, error: 'Please enter a name.' });
      return;
    }
    setState({ saving: true, error: null });
    try {
      await renameLiviDocument(doc.documentID, trimmed);
      onSaved();
    } catch (err) {
      setState({ saving: false, error: actionError(err, 'rename') });
    }
  };

  return (
    <Dialog open={!!doc} onClose={onClose} maxWidth="sm" fullWidth aria-labelledby="rename-file-title">
      <form className={styles.dialogBody} onSubmit={save}>
        <h2 id="rename-file-title" className={styles.dialogTitle}>Rename file</h2>
        <label className={styles.field}>
          <span className={styles.fieldLabel}>Name</span>
          <input
            className={styles.input}
            value={title}
            maxLength={200}
            onChange={(e) => setTitle(e.target.value)}
            autoFocus
          />
        </label>
        {state.error && <p className={styles.errorBanner} role="alert">{state.error}</p>}
        <div className={styles.dialogActions}>
          <button type="button" className={styles.secondaryBtn} onClick={onClose} disabled={state.saving}>Cancel</button>
          <button type="submit" className={styles.primaryBtn} disabled={state.saving}>
            {state.saving ? 'Saving…' : 'Save'}
          </button>
        </div>
      </form>
    </Dialog>
  );
}

/**
 * Livi Files — the Word and Excel files Livi created on this case, with
 * preview, download, rename and delete. Delete hides the file; it stays in the
 * case's storage. See .claude/references/livi-documents.md (ADR-001, D5).
 */
export default function LiviFilesPage() {
  const { userProfile } = useContext(UserProfileContext) || {};
  const caseId = userProfile?.currentCase?.caseID;
  const [format, setFormat] = useState('');
  const { data, error, isLoading, mutate } = useLiviDocuments(caseId, { format });
  const { bucketName } = useCaseBucketName(caseId);
  const [openDoc, setOpenDoc] = useState(null);
  const [renameDoc, setRenameDoc] = useState(null);
  const [actionMessage, setActionMessage] = useState(null);

  const doDelete = useCallback(async (doc) => {
    setActionMessage(null);
    try {
      await deleteLiviDocument(doc.documentID);
    } catch (err) {
      setActionMessage(actionError(err, 'delete'));
      mutate();
    }
  }, [mutate]);

  const { handleConfirmAction: confirmDelete, ConfirmDialogElement } = useConfirmAction(doDelete, {
    title: 'Delete file',
    getEntityName: (doc) => doc.title,
    confirmMessage: 'The file is removed from Livi Files. Livisibly keeps a copy in the case records.',
    proceedButtonLabel: 'Delete',
    destructive: true,
  });

  const documents = data?.documents || [];

  let content;
  if (!caseId) {
    content = <Card padding="md"><p className={styles.emptyText}>Select a case to see its files.</p></Card>;
  } else if (error) {
    content = <p className={styles.errorBanner} role="alert">The files could not be loaded. Please refresh the page.</p>;
  } else if (isLoading) {
    content = <p className={styles.emptyText}>Loading…</p>;
  } else if (documents.length === 0) {
    content = (
      <Card padding="md">
        <p className={styles.emptyText}>
          {format ? 'No files of this type yet.' : 'No files yet. Ask Livi in a case chat for a Word or Excel file.'}
        </p>
      </Card>
    );
  } else {
    content = (
      <Card padding="none" className={styles.tableCard}>
        <table className={styles.table}>
          <thead>
            <tr>
              <th className={styles.th}>Name</th>
              <th className={styles.th}>Type</th>
              <th className={styles.th}>Size</th>
              <th className={styles.th}>Created</th>
              <th className={styles.th}>Sources</th>
              <th className={styles.th}><span className={styles.visuallyHidden}>Actions</span></th>
            </tr>
          </thead>
          <tbody>
            {documents.map((doc) => {
              const created = toDate(doc.created);
              return (
                <tr key={doc.documentID} className={styles.row}>
                  <td className={styles.td}>
                    <button type="button" className={styles.linkBtn} onClick={() => setOpenDoc(doc)}>
                      {doc.title || 'Untitled'}
                    </button>
                    {doc.summary && <div className={styles.muted}>{doc.summary}</div>}
                  </td>
                  <td className={styles.td}>
                    <Badge size="sm" variant="info">{DOCUMENT_FORMATS[doc.format]?.label || doc.format}</Badge>
                  </td>
                  <td className={`${styles.td} ${styles.nowrap}`}>{formatFileSize(doc.sizeBytes)}</td>
                  <td className={`${styles.td} ${styles.nowrap}`}>{created ? formatDateTime(created) : ''}</td>
                  <td className={styles.td}>{Number(doc.sourceCount) || 0}</td>
                  <td className={`${styles.td} ${styles.rowActions}`}>
                    <button type="button" className={styles.secondaryBtn} onClick={() => setRenameDoc(doc)}
                            aria-label={`Rename ${doc.title || 'file'}`}>
                      Rename
                    </button>
                    <button type="button" className={styles.dangerBtn} onClick={() => confirmDelete(doc)}
                            aria-label={`Delete ${doc.title || 'file'}`}>
                      Delete
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </Card>
    );
  }

  return (
    <div className={styles.page}>
      <PageHeader title="Livi Files" subtitle="Word and Excel files Livi created on this case" />
      <div className={styles.toolbar}>
        <label className={styles.field}>
          <span className={styles.fieldLabel}>Show</span>
          <select className={styles.select} value={format} onChange={(e) => setFormat(e.target.value)}>
            {FORMAT_FILTERS.map((f) => <option key={f.value} value={f.value}>{f.label}</option>)}
          </select>
        </label>
      </div>
      {actionMessage && <p className={styles.errorBanner} role="alert">{actionMessage}</p>}
      {content}

      <Dialog open={!!openDoc} onClose={() => setOpenDoc(null)} maxWidth="lg" fullWidth
              aria-label={openDoc?.title ? `Preview: ${openDoc.title}` : 'File preview'}>
        {openDoc && (
          <div className={styles.previewDialog}>
            <div className={styles.previewClose}>
              <button type="button" className={styles.secondaryBtn} onClick={() => setOpenDoc(null)}>Close</button>
            </div>
            <DocumentPanel
              documentId={openDoc.documentID}
              title={openDoc.title}
              format={openDoc.format}
              filename={openDoc.filename}
              s3Key={openDoc.s3Key}
              bucket={bucketName}
              sizeBytes={openDoc.sizeBytes}
              created={openDoc.created}
              sourceCount={openDoc.sourceCount}
            />
          </div>
        )}
      </Dialog>
      {renameDoc && (
        <RenameDialog
          doc={renameDoc}
          onClose={() => setRenameDoc(null)}
          onSaved={() => setRenameDoc(null)}
        />
      )}
      <ConfirmDialogElement />
    </div>
  );
}
